import { useEffect, useMemo } from 'react';

import debounce from 'lodash/debounce';
import { useFormState } from 'react-final-form';
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

type UseFormPersistenceOptions = {
  storageKey: string;
  debounceMs?: number;
  enabled?: boolean;
};

type FormPersistenceStore<T> = {
  values: T;
  setValues: (values: T) => void;
  clearValues: () => void;
};

const storeCache = new Map<
  string,
  ReturnType<ReturnType<typeof create<FormPersistenceStore<any>>>>
>();

const createFormPersistenceStore = <T>(
  storageKey: string,
  defaultValues: T
) => {
  if (storeCache.has(storageKey)) {
    return storeCache.get(storageKey)!;
  }

  const store = create<FormPersistenceStore<T>>()(
    persist(
      (set) => ({
        values: defaultValues,
        setValues: (values: T) => set({ values }),
        clearValues: () => set({ values: defaultValues }),
      }),
      {
        name: storageKey,
        storage: createJSONStorage(() => sessionStorage),
      }
    )
  );

  storeCache.set(storageKey, store);
  return store;
};

export const useFormPersistence = <T>(
  defaultValues: T,
  {
    storageKey,
    enabled = true,
  }: Omit<UseFormPersistenceOptions, 'debounceMs' | 'exclude'>
) => {
  const useStore = createFormPersistenceStore(storageKey, defaultValues);
  const { values, setValues, clearValues } = useStore();

  const getInitialValues = (): T => {
    return enabled ? values : defaultValues;
  };

  const clearPersistedValues = () => {
    if (!enabled) {
      return;
    }
    clearValues();
  };

  return {
    getInitialValues,
    clearPersistedValues,
    setPersistedValues: setValues,
  };
};

/** Hook to use inside the form to auto-persist values */
const usePersistFormValues = <T>({
  storageKey,
  debounceMs = 500,
  enabled = true,
}: UseFormPersistenceOptions) => {
  const { values } = useFormState<T>({
    subscription: { values: true },
  });
  const useStore = createFormPersistenceStore(storageKey, values);
  const { setValues } = useStore();

  const debouncedPersist = useMemo(
    () => debounce(setValues, debounceMs),
    [setValues, debounceMs]
  );

  useEffect(() => {
    if (!enabled || !values) {
      return;
    }
    debouncedPersist(values);
  }, [values, enabled, debouncedPersist]);

  useEffect(() => {
    return () => {
      debouncedPersist.cancel();
    };
  }, [debouncedPersist]);
};

export const useFormPersistenceCleanup = ({
  storageKey,
}: Pick<UseFormPersistenceOptions, 'storageKey'>) => {
  useEffect(() => {
    return () => {
      sessionStorage.removeItem(storageKey);
      storeCache.delete(storageKey);
    };
  }, [storageKey]);
};

export const FormPersistence = ({
  config,
}: {
  config: UseFormPersistenceOptions;
}) => {
  usePersistFormValues(config);
  return null;
};
